import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { GraduationCap, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeacherSetup = () => {
  const navigate = useNavigate();
  const [teacherName, setTeacherName] = useState('');
  const [email, setEmail] = useState('');
  const [school, setSchool] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!teacherName.trim() || !email.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post(`${API}/teacher/register`, {
        teacher_name: teacherName,
        email: email,
        school: school
      });

      // Store teacher info in localStorage
      localStorage.setItem('userRole', 'teacher');
      localStorage.setItem('teacherId', response.data.teacher_id);
      localStorage.setItem('teacherName', teacherName);
      
      navigate('/teacher-dashboard');
    } catch (err) {
      console.error('Error registering teacher:', err);
      setError(err.response?.data?.detail || 'Failed to register. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    }}>
      <div className="max-w-2xl w-full">
        <Button
          onClick={() => navigate('/role-selection')}
          variant="ghost"
          className="mb-6 text-lg font-semibold text-white hover:scale-110 transition-transform"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <Card className="p-8 md:p-12 bg-white/95 backdrop-blur-sm shadow-2xl bounce-in" style={{
          borderRadius: '32px'
        }}>
          <div className="text-center mb-8">
            <GraduationCap className="w-16 h-16 mx-auto mb-4 text-green-500" />
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Teacher Account Setup</h2>
            <p className="text-gray-600 text-lg">Track your students' learning journey</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-lg font-semibold text-gray-700 mb-2">Your Name *</label>
              <Input
                type="text"
                placeholder="Enter your name..."
                value={teacherName}
                onChange={(e) => setTeacherName(e.target.value)}
                className="text-xl p-6 border-4 border-green-300 focus:border-green-500"
                style={{ borderRadius: '16px' }}
              />
            </div>

            <div>
              <label className="block text-lg font-semibold text-gray-700 mb-2">Email Address *</label>
              <Input
                type="email"
                placeholder="your.email@school.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="text-xl p-6 border-4 border-green-300 focus:border-green-500"
                style={{ borderRadius: '16px' }}
              />
            </div>

            <div>
              <label className="block text-lg font-semibold text-gray-700 mb-2">School Name (Optional)</label>
              <Input
                type="text"
                placeholder="Your school name..."
                value={school}
                onChange={(e) => setSchool(e.target.value)}
                className="text-xl p-6 border-4 border-green-300 focus:border-green-500"
                style={{ borderRadius: '16px' }}
              />
            </div>

            {error && (
              <Card className="p-4 bg-red-50 border-2 border-red-300" style={{ borderRadius: '12px' }}>
                <p className="text-red-700 text-center">{error}</p>
              </Card>
            )}

            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full py-6 text-xl font-bold transition-all duration-300 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                borderRadius: '16px'
              }}
            >
              {loading ? 'Creating Account...' : 'Continue as Teacher'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TeacherSetup;
